import z from 'zod/v4'
// Import ParsedAsset from assets module to avoid circular dependency
import type { ParsedAsset } from '../assets/assetTypes.ts'
import { type Tables } from '../db/supabase.types.ts'
import { locationSchema, type Location } from '../geography/locationTypes.ts'
import { type RespondedQuestionnaireAnswers } from '../questionnaires/types.ts'
import {
  propertyFilterSchema,
  validatedPropertyFilterSchema,
  type PropertyFiltersCoreType,
} from './filterTypes.ts'

export type ReadinessValue = 0 | 0.3 | 0.6 | 0.8 | 1

export type LeadReadiness = {
  value: ReadinessValue
  mostRecentDate: string
}

export type InterestConfigType = 'locations' | PropertyFiltersCoreType

const locationsSchema = z.array(locationSchema)

const looseAnswerSchema = z.object({ id: z.string(), value: z.union([z.string(), z.number()]) })
const looseAnswersSchema = z.array(z.unknown()).transform(
  (items): RespondedQuestionnaireAnswers =>
    items.flatMap((item) => {
      const result = looseAnswerSchema.safeParse(item)
      return result.success ? [result.data] : []
    })
)

const filteredLocationsSchema = z.array(z.unknown()).transform((items): Location[] => {
  const validLocations: Location[] = []

  for (const item of items) {
    const result = locationSchema.safeParse(item)

    if (result.success) {
      validLocations.push(result.data)
    }
  }

  return validLocations
})

export const validatedPropertyInterestDataSchema = z.object({
  filters: validatedPropertyFilterSchema,
  locations: locationsSchema.min(1),
  description: z.string().nullish().default(null),
})

export const propertyInterestDataSchema = z.object({
  answers: looseAnswersSchema.optional().default([]),
  filters: propertyFilterSchema.default({}),
  locations: filteredLocationsSchema.default([]),
  description: z.string().nullable().optional(),
})

export type PropertyInterestData = z.infer<typeof propertyInterestDataSchema>

export const parsedInterestSchema = z.object({
  id: z.string(),
  user_id: z.string().nullable(),
  data: propertyInterestDataSchema,
  description: z.string().nullable(),
  created_at: z.date(),
  updated_at: z.date().nullable(),
  deleted_at: z.date().nullable(),
  verified_at: z.date().nullable(),
})

export type RawInterest = Omit<Tables<'interests'>, 'embedding'>

export type ParsedInterest = z.infer<typeof parsedInterestSchema>

export type ContactInterestStatus = {
  verifiedInterest: ParsedInterest | null
  realtorInterest: ParsedInterest | null
  updatedAt: Date | null
  canEdit: boolean
}

export type InterestAssetMatchScore = {
  asset_id: string
  match_score: number | null // 0-4 median
  price_score: number | null // 0-4
  location_score: number | null // 0-4
  size_score: number | null // 0-4
  building_type_score: number | null // 0-4
  other_score: number | null // 0-4
}

export type AssetWithMatchScore = {
  asset: ParsedAsset
  matchScores: InterestAssetMatchScore
}
